import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Alert,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { JobCardFormA4 } from './JobCardFormA4';
import { FormData, Vehicle, JobCard, VehicleDamage } from '../../types';
import { useMaintenanceStore } from '../../stores/maintenanceStore';
import { useAuthStore } from '../../stores/authStore';
import { useJobCardContext } from '../../contexts/JobCardContext';
import { supabase } from '../../api/supabase';
import { useNavigationContext } from '../../navigation/NavigationContext';
import { PERMISSIONS } from '../../constants/permissions';
import theme from '../../theme';

interface JobCardFormWrapperProps {
  navigation?: any;
  editingCard?: JobCard | null;
  selectedVehicle?: Vehicle | null;
  onChangeVehicle?: () => void;
  mode?: 'create' | 'edit' | 'view';
  existingJobCard?: JobCard;
}

// Utility function to format vehicle plate
const formatVehiclePlate = (vehicle: Vehicle): string => {
  if (!vehicle.licensePlate) return 'N/A';
  
  const plate = vehicle.licensePlate || '';
  
  return plate;
};

export const JobCardFormWrapper: React.FC<JobCardFormWrapperProps> = ({
  navigation,
  editingCard = null,
  selectedVehicle = null,
  onChangeVehicle,
  mode = 'create',
  existingJobCard,
}) => {
  const { createNewJobCard, updateJobCard, loading } = useMaintenanceStore();
  const { setCurrentRoute } = useNavigationContext();
  const { setEditingJobCard } = useJobCardContext();
  const { user } = useAuthStore();
  const isWeb = Platform.OS === 'web';
  const isViewMode = mode === 'view';
  
  // Use existingJobCard if in view mode, otherwise use editingCard
  const cardToDisplay = isViewMode ? existingJobCard : editingCard;
  const [vehicleDatabase, setVehicleDatabase] = useState<Vehicle[]>([]);
  const [currentVehicle, setCurrentVehicle] = useState<Vehicle | null>(selectedVehicle);
  const [fuelLevel, setFuelLevel] = useState(50);
  const [vehicleDamages, setVehicleDamages] = useState<VehicleDamage[]>([]);
  const [maintenanceSignatureSvg, setMaintenanceSignatureSvg] = useState<string>('');
  const [workshopSignatureSvg, setWorkshopSignatureSvg] = useState<string>('');

  const [formData, setFormData] = useState<FormData>({
    jobNumber: '',
    requestDate: new Date().toISOString().split('T')[0],
    requiredBy: '',
    requestedBy: user?.name || user?.email || '',
    licensePlate: '',
    makeModel: '',
    year: '',
    mileage: '',
    vin: '',
    keys: 'yes',
    keysYes: true,
    keysNo: false,
    driver: '',
    jobDescription: '',
    notes: '',
    conditionNotes: '',
    issues: '',
    completedBy: '',
    handoverDate: new Date().toISOString().split('T')[0],
    returnDate: '',
    handedBy: '',
    receivedBy: 'Nazir Ahmed',
    workDescription: '',
    maintenanceSignature: '',
    workshopSignature: '',
    // Work type checkboxes
    underMaintenance: false,
    accidentRepair: false,
    underMaintenanceAlQouz: false,
    underMaintenanceAgency: false,
    others: false,
    // Vehicle condition checkboxes
    lightsOk: true,
    wipersOk: true,
    hornOk: true,
    acOk: true,
    tiresOk: true,
    mirrorsOk: true,
    // Signature fields
    ourSignature: '',
    emiratesSignature: '',
    // Job status
    jobStatus: 'in_progress', // Changed from 'pending' to match database constraint
  });

  // Load vehicle database on component mount
  useEffect(() => {
    loadVehicleDatabase();
  }, []);

  // Auto-generate job number when creating new job card
  useEffect(() => {
    if (!isViewMode && !cardToDisplay && !formData.jobNumber && currentVehicle) {
      generateJobNumber();
    }
  }, [cardToDisplay, currentVehicle, isViewMode]);

  const generateJobNumber = async () => {
    try {
      if (!currentVehicle) {
        console.warn('⚠️ Cannot generate job number without vehicle selected');
        return;
      }

      // Extract plate number from vehicle
      const plate = currentVehicle.licensePlate || '';
      
      // Get current date in YYYY-MM-DD format
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      // Format: WS-[PLATE]-YYYY-MM-DD
      const jobNumber = `WS-${plate}-${dateStr}`;
      
      console.log('🔢 Auto-generated job number:', jobNumber);
      setFormData(prev => ({ ...prev, jobNumber }));
    } catch (error) {
      console.error('Error generating job number:', error);
      // Fallback to timestamp
      const timestamp = Date.now().toString().slice(-6);
      setFormData(prev => ({ ...prev, jobNumber: `WS-${timestamp}` }));
    }
  };

  // Load editing card data if provided
  useEffect(() => {
    if (cardToDisplay) {
      setFormData({
        jobNumber: cardToDisplay.jobNumber || '',
        requestDate: cardToDisplay.requestDate || new Date().toISOString().split('T')[0],
        requiredBy: cardToDisplay.requiredBy || '',
        requestedBy: cardToDisplay.requestedBy || '',
        licensePlate: cardToDisplay.licensePlate || '',
        makeModel: cardToDisplay.makeModel || '',
        year: cardToDisplay.year || '',
        mileage: cardToDisplay.mileage || '',
        vin: cardToDisplay.vin || '',
        keys: cardToDisplay.keys || 'yes',
        keysYes: cardToDisplay.keysYes || cardToDisplay.keys === 'yes',
        keysNo: cardToDisplay.keysNo || cardToDisplay.keys === 'no',
        driver: cardToDisplay.driver || '',
        jobDescription: cardToDisplay.jobDescription || '',
        notes: cardToDisplay.notes || '',
        conditionNotes: cardToDisplay.conditionNotes || '',
        issues: cardToDisplay.issues || '',
        completedBy: cardToDisplay.completedBy || '',
        handoverDate: cardToDisplay.handoverDate || new Date().toISOString().split('T')[0],
        returnDate: cardToDisplay.returnDate || '',
        handedBy: cardToDisplay.handedBy || '',
        receivedBy: cardToDisplay.receivedBy || '',
        workDescription: cardToDisplay.workDescription || '',
        maintenanceSignature: cardToDisplay.maintenanceSignature || '',
        workshopSignature: cardToDisplay.workshopSignature || '',
        // Work type checkboxes
        underMaintenance: cardToDisplay.underMaintenance || false,
        accidentRepair: cardToDisplay.accidentRepair || false,
        underMaintenanceAlQouz: cardToDisplay.underMaintenanceAlQouz || false,
        underMaintenanceAgency: cardToDisplay.underMaintenanceAgency || false,
        others: cardToDisplay.others || false,
        // Vehicle condition checkboxes
        lightsOk: cardToDisplay.lightsOk !== undefined ? cardToDisplay.lightsOk : true,
        wipersOk: cardToDisplay.wipersOk !== undefined ? cardToDisplay.wipersOk : true,
        hornOk: cardToDisplay.hornOk !== undefined ? cardToDisplay.hornOk : true,
        acOk: cardToDisplay.acOk !== undefined ? cardToDisplay.acOk : true,
        tiresOk: cardToDisplay.tiresOk !== undefined ? cardToDisplay.tiresOk : true,
        mirrorsOk: cardToDisplay.mirrorsOk !== undefined ? cardToDisplay.mirrorsOk : true,
        // Signature fields
        ourSignature: cardToDisplay.ourSignature || '',
        emiratesSignature: cardToDisplay.emiratesSignature || '',
        // Job status
        jobStatus: cardToDisplay.jobStatus || 'pending',
      });
      setFuelLevel(cardToDisplay.fuelLevel || 50);
      
      // Load new fields
      setVehicleDamages(cardToDisplay.vehicleDamages || []);
      setMaintenanceSignatureSvg(cardToDisplay.maintenanceSignatureSvg || '');
      setWorkshopSignatureSvg(cardToDisplay.workshopSignatureSvg || '');
    }
  }, [cardToDisplay, existingJobCard, editingCard]); // Fixed: Include all dependencies

  // Auto-fill vehicle data when selectedVehicle changes
  useEffect(() => {
    if (selectedVehicle && !editingCard) {
      const plate = selectedVehicle.licensePlate || 'N/A';
      // Don't add vehicle code prefix - use plate number only
      
      setFormData(prev => ({
        ...prev,
        licensePlate: plate,
        makeModel: selectedVehicle.makeModel || '',
        year: String(selectedVehicle.year || ''),
        vin: selectedVehicle.vin || selectedVehicle.vinNumber || selectedVehicle.chassisNumber || '',
        mileage: String(selectedVehicle.currentMileage || selectedVehicle.odometerReading || ''),
      }));
      setCurrentVehicle(selectedVehicle);
      
      console.log('✅ Auto-filled vehicle data:', {
        plate: plate,
        makeModel: selectedVehicle.makeModel,
        year: selectedVehicle.year,
        vin: selectedVehicle.vin,
      });
    }
  }, [selectedVehicle, editingCard]);

  const loadVehicleDatabase = async () => {
    try {
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .order('license_plate'); // Use snake_case for database column

      if (error) {
        console.error('Error loading vehicles:', error);
        return;
      }

      // Map database fields to camelCase for app use
      const mappedVehicles = (data || []).map(v => ({
        ...v,
        licensePlate: v.license_plate,
        vehicleCode: v.vehicle_code,
        makeModel: v.make_model,
        carStatus: v.car_status,
        currentDriverId: v.current_driver_id,
        dailyRent: v.daily_rental_rate,
        currentMileage: v.odometer_reading,
      }));

      setVehicleDatabase(mappedVehicles as any);
    } catch (error) {
      console.error('Error loading vehicle database:', error);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleKeyCheckboxChange = (selectedKey: 'yes' | 'no') => {
    setFormData(prev => ({
      ...prev,
      keys: selectedKey,
      keysYes: selectedKey === 'yes',
      keysNo: selectedKey === 'no'
    }));
  };

  const handleVehicleSelect = (vehicle: Vehicle) => {
    // Fill all vehicle fields from the selected vehicle
    setFormData(prev => ({
      ...prev,
      licensePlate: formatVehiclePlate(vehicle),
      makeModel: vehicle.makeModel || '',
      year: String(vehicle.year || ''),
      vin: vehicle.vin || vehicle.vinNumber || vehicle.chassisNumber || '',
    }));
    
    setCurrentVehicle(vehicle);
  };

  const handleSave = async () => {
    console.log('💾 Save button clicked!');
    console.log('📝 Form data:', {
      jobNumber: formData.jobNumber,
      licensePlate: formData.licensePlate,
      makeModel: formData.makeModel,
    });

    // Validate required fields
    if (!formData.jobNumber) {
      console.log('❌ Validation failed - job number missing (this should not happen!)');
      if (Platform.OS === 'web') {
        window.alert('Job number was not generated. Please try again.');
      } else {
        Alert.alert('Error', 'Job number was not generated. Please try again.');
      }
      // Try to generate it again
      await generateJobNumber();
      return;
    }
    
    if (!formData.licensePlate || !formData.makeModel) {
      console.log('❌ Validation failed - missing vehicle information');
      if (Platform.OS === 'web') {
        window.alert('Please select a vehicle first.');
      } else {
        Alert.alert('Validation Error', 'Please select a vehicle first.');
      }
      return;
    }

    // Validate at least one work type is selected
    const hasWorkType = formData.underMaintenance || formData.accidentRepair || 
                        formData.underMaintenanceAlQouz || formData.underMaintenanceAgency || 
                        formData.others;
    if (!hasWorkType) {
      console.log('❌ Validation failed - no work type selected');
      if (Platform.OS === 'web') {
        window.alert('Please select at least one Type of Work.');
      } else {
        Alert.alert('Validation Error', 'Please select at least one Type of Work.');
      }
      return;
    }

    console.log('✅ Validation passed, attempting to save...');

    try {
      if (editingCard) {
        // For UPDATE: Use snake_case (database format)
        const updateData: any = {
          job_number: formData.jobNumber,
          request_date: formData.requestDate,
          required_by: formData.requiredBy,
          requested_by: formData.requestedBy,
          license_plate: formData.licensePlate,
          make_model: formData.makeModel,
          year: formData.year,
          vin: formData.vin,
          mileage: formData.mileage,
          keys: formData.keys,
          driver: formData.driver,
          job_description: formData.jobDescription,
          work_description: formData.workDescription,
          notes: formData.notes,
          condition_notes: formData.conditionNotes,
          issues: formData.issues,
          completed_by: formData.completedBy,
          handover_date: formData.handoverDate,
          return_date: formData.returnDate,
          handed_by: formData.handedBy,
          received_by: formData.receivedBy,
          job_status: formData.jobStatus || 'in_progress',
          fuel_level: fuelLevel,
          lights_ok: formData.lightsOk,
          wipers_ok: formData.wipersOk,
          horn_ok: formData.hornOk,
          ac_ok: formData.acOk,
          tires_ok: formData.tiresOk,
          mirrors_ok: formData.mirrorsOk,
          under_maintenance: formData.underMaintenance || false,
          accident_repair: formData.accidentRepair || false,
          under_maintenance_al_qouz: formData.underMaintenanceAlQouz || false,
          under_maintenance_agency: formData.underMaintenanceAgency || false,
          others: formData.others || false,
          vehicle_damages: vehicleDamages,
          maintenance_signature_svg: maintenanceSignatureSvg,
          workshop_signature_svg: workshopSignatureSvg,
        };

        console.log('🔄 Updating existing job card:', editingCard.id);
        console.log('📦 Update data prepared:', Object.keys(updateData));
        await updateJobCard(String(editingCard.id), updateData);
        console.log('✅ Update successful');
        
        // Clear editing context on web
        if (isWeb) {
          setEditingJobCard(null);
        }
        
        // Navigate back to job cards list
        if (isWeb) {
          console.log('🌐 Web: Navigating to JobCards using setCurrentRoute');
          window.alert('Job card updated successfully!');
          setCurrentRoute('MaintenanceJobCards');
        } else {
          console.log('📱 Mobile: Showing alert and navigating back');
          Alert.alert(
            'Success',
            'Job card updated successfully!',
            [{ text: 'OK', onPress: () => navigation?.goBack?.() }]
          );
        }
      } else {
        // For CREATE: Use camelCase (API function expects this)
        const createData = {
          jobNumber: formData.jobNumber,
          requestDate: formData.requestDate,
          requiredBy: formData.requiredBy,
          requestedBy: formData.requestedBy,
          licensePlate: formData.licensePlate,
          makeModel: formData.makeModel,
          year: formData.year,
          vin: formData.vin,
          mileage: formData.mileage,
          keys: formData.keys,
          driver: formData.driver,
          jobDescription: formData.jobDescription,
          workDescription: formData.workDescription,
          notes: formData.notes,
          conditionNotes: formData.conditionNotes,
          issues: formData.issues,
          completedBy: formData.completedBy,
          handoverDate: formData.handoverDate,
          returnDate: formData.returnDate,
          handedBy: formData.handedBy,
          receivedBy: formData.receivedBy,
          jobStatus: formData.jobStatus || 'in_progress',
          fuelLevel,
          lightsOk: formData.lightsOk,
          wipersOk: formData.wipersOk,
          hornOk: formData.hornOk,
          acOk: formData.acOk,
          tiresOk: formData.tiresOk,
          mirrorsOk: formData.mirrorsOk,
          underMaintenance: formData.underMaintenance || false,
          accidentRepair: formData.accidentRepair || false,
          underMaintenanceAlQouz: formData.underMaintenanceAlQouz || false,
          underMaintenanceAgency: formData.underMaintenanceAgency || false,
          others: formData.others || false,
          vehicleDamages,
          maintenanceSignatureSvg,
          workshopSignatureSvg,
        };

        console.log('➕ Creating new job card...');
        console.log('📦 Create data prepared:', Object.keys(createData));
        
        // Check CREATE_JOB_CARDS permission before creating
        const hasCreatePermission = useAuthStore.getState().hasPermission(PERMISSIONS.CREATE_JOB_CARDS);
        if (!hasCreatePermission) {
          const deniedMessage = isWeb 
            ? 'You do not have permission to create job cards.\n\nليس لديك صلاحية لإنشاء بطاقات الأعمال.'
            : 'You do not have permission to create job cards.\n\nليس لديك صلاحية لإنشاء بطاقات الأعمال.';
          
          if (isWeb) {
            window.alert(deniedMessage);
          } else {
            Alert.alert('Permission Denied / الإذن مرفوض', deniedMessage);
          }
          return;
        }
        
        await createNewJobCard(createData as any);
        console.log('✅ Creation successful');
        
        // Update vehicle status based on job card status
        if (selectedVehicle?.id) {
          const { supabase } = require('../../api/supabase');
          
          // Determine vehicle status based on job status
          let newCarStatus = 'in_workshop';
          if (formData.jobStatus === 'completed' || formData.jobStatus === 'handed_over') {
            newCarStatus = 'available';
            console.log('🏁 Job completed - Setting vehicle status to available...');
          } else {
            console.log('🔧 Job active - Setting vehicle status to in_workshop...');
          }
          
          try {
            const { error: statusError } = await supabase
              .from('vehicles')
              .update({ car_status: newCarStatus })
              .eq('id', selectedVehicle.id);
            
            if (statusError) {
              console.error('⚠️ Failed to update vehicle status:', statusError);
            } else {
              console.log(`✅ Vehicle status updated to ${newCarStatus}`);
            }
          } catch (statusUpdateError) {
            console.error('⚠️ Error updating vehicle status:', statusUpdateError);
          }
        }
        
        // Clear editing context on web
        if (isWeb) {
          setEditingJobCard(null);
        }
        
        // Navigate back to job cards list
        if (isWeb) {
          console.log('🌐 Web: Navigating to JobCards using setCurrentRoute');
          window.alert('Job card created successfully!');
          setCurrentRoute('MaintenanceJobCards');
        } else {
          console.log('📱 Mobile: Showing alert and navigating back');
          Alert.alert(
            'Success',
            'Job card created successfully!',
            [{ text: 'OK', onPress: () => navigation?.goBack?.() }]
          );
        }
      }
    } catch (error) {
      console.error('❌ Save error:', error);
      if (Platform.OS === 'web') {
        window.alert(editingCard ? 'Failed to update job card.' : 'Failed to create job card.');
      } else {
        Alert.alert('Error', editingCard ? 'Failed to update job card.' : 'Failed to create job card.');
      }
    }
  };

  const handleClear = () => {
    Alert.alert(
      'Clear Form',
      'Are you sure you want to clear all form data?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Clear',
          style: 'destructive',
          onPress: () => {
            setFormData({
              jobNumber: '',
              requestDate: new Date().toISOString().split('T')[0],
              requiredBy: '',
              requestedBy: '',
              licensePlate: '',
              makeModel: '',
              year: '',
              mileage: '',
              vin: '',
              keys: 'yes',
              keysYes: true,
              keysNo: false,
              driver: '',
              jobDescription: '',
              notes: '',
              conditionNotes: '',
              issues: '',
              completedBy: '',
              handoverDate: new Date().toISOString().split('T')[0],
              returnDate: '',
              handedBy: '',
              receivedBy: '',
              workDescription: '',
              maintenanceSignature: '',
              workshopSignature: '',
              // Work type checkboxes
              underMaintenance: false,
              accidentRepair: false,
              underMaintenanceAlQouz: false,
              underMaintenanceAgency: false,
              others: false,
              // Vehicle condition checkboxes
              lightsOk: true,
              wipersOk: true,
              hornOk: true,
              acOk: true,
              tiresOk: true,
              mirrorsOk: true,
              // Signature fields
              ourSignature: '',
              emiratesSignature: '',
              // Job status
              jobStatus: 'in_progress', // Changed from 'pending' to match database constraint
            });
            setFuelLevel(50);
            setCurrentVehicle(null);
            setVehicleDamages([]);
            setMaintenanceSignatureSvg('');
            setWorkshopSignatureSvg('');
          }
        }
      ]
    );
  };

  return (
    <View style={styles.container}>
      <JobCardFormA4
        formData={formData}
        onInputChange={handleInputChange}
        fuelLevel={fuelLevel}
        onFuelLevelChange={setFuelLevel}
        onKeyCheckboxChange={handleKeyCheckboxChange}
        vehicleDatabase={vehicleDatabase}
        onVehicleSelect={handleVehicleSelect}
        vehicleDamages={vehicleDamages}
        onVehicleDamagesChange={setVehicleDamages}
        maintenanceSignatureSvg={maintenanceSignatureSvg}
        onMaintenanceSignatureChange={setMaintenanceSignatureSvg}
        workshopSignatureSvg={workshopSignatureSvg}
        onWorkshopSignatureChange={setWorkshopSignatureSvg}
        readOnly={isViewMode}
      />
      
      {/* Vehicle Info Banner (if vehicle selected) */}
      {(selectedVehicle || currentVehicle) && onChangeVehicle && (
        <View style={styles.vehicleBanner}>
          <View style={styles.vehicleBannerContent}>
            <Ionicons name="car-sport" size={20} color={theme.colors.primary} />
            <Text style={styles.vehicleBannerText}>
              {formData.licensePlate} - {formData.makeModel}
            </Text>
          </View>
          <TouchableOpacity onPress={onChangeVehicle} style={styles.changeVehicleButton}>
            <Ionicons name="swap-horizontal" size={20} color={theme.colors.primary} />
            <Text style={styles.changeVehicleText}>Change Vehicle</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Form Controls - Hide in view mode */}
      {!isViewMode && (
        <View style={styles.formControls}>
          <TouchableOpacity
            style={[styles.button, styles.saveButton]}
            onPress={() => {
              console.log('🔘 Save button pressed!');
              handleSave();
            }}
            disabled={loading}
          >
            <Text style={styles.buttonText}>
              {loading ? (cardToDisplay ? 'Updating...' : 'Creating...') : (cardToDisplay ? 'Update Job Card' : 'Save Job Card')}
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.button, styles.clearButton]}
            onPress={handleClear}
            disabled={loading}
          >
            <Text style={[styles.buttonText, styles.clearButtonText]}>Clear Form</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  vehicleBanner: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: '#e3f2fd',
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
  },
  vehicleBannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  vehicleBannerText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  changeVehicleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.sm,
  },
  changeVehicleText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.primary,
    fontWeight: '600' as any,
  },
  formControls: {
    flexDirection: 'row',
    padding: 20,
    backgroundColor: theme.colors.white,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
    gap: 15,
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  saveButton: {
    backgroundColor: '#546E7A',
  },
  clearButton: {
    backgroundColor: '#dc3545',
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  clearButtonText: {
    color: 'white',
  },
});

export default JobCardFormWrapper;
